import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { CompanyService, GenerationJobsService, RetrieveDocumentGenerationJob } from 'api';
import { useResponse } from 'services/hooks';
import { POLL_TYPE, useAppLoadAsyncOperations } from 'services/hooks/useAppLoadAsyncActions';
import DocumentGenerationJobTableItemFinMapMenuProps from './DocumentGenerationJobTableItemFinMapMenu.props';
import { useStore } from '../../../../../../common/store';
import { UseStoreValues } from '../../../../../../common/types/store';
import { Logger } from '../../../../../../utilities';
import { JSONGenerationJobParsingMode } from '../../../../models/enums/generation-job-parsing-mode.enum';

const DocumentGenerationJobTableItemFinMapMenu = ({
  generationJob,
  onClose,
}: DocumentGenerationJobTableItemFinMapMenuProps) => {
  const [loading, setLoading] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const { successNotification, errorNotification } = useResponse();
  const { manuallyExecutePoll } = useAppLoadAsyncOperations(false);
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;

  const handleCreateMapping = async () => {
    setLoading(true);

    try {
      Logger.info(`Creating attribute mapping for job ${generationJob.job_id}`);

      await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(generationJob.job_id);

      successNotification('Financial mapping created and associated with financial period');

      // Close menu after successful creation
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      Logger.error('Error creating attribute mapping:', err);
      const errorMessage = err?.body?.detail || err?.message || 'Failed to create mapping';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessClick = () => {
    setReprocessDialogOpen(true);
  };

  const handleReprocessConfirm = async () => {
    setReprocessDialogOpen(false);
    setLoading(true);

    try {
      Logger.info(`Reprocessing document for job ${generationJob.job_id}`);

      if (!companyInfo?.id) {
        errorNotification('Company information not available');
        return;
      }

      // Get the document reference and measurement date from metadata
      const { document_reference_id, measurement_date_id, file_id } = generationJob.metadata;

      // Create a new generation job for the same document
      await CompanyService.apiCompanyDocumentGenerationJobsNewCreate(companyInfo.id?.toString() || '', {
        processing_type: RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING,
        reference_files: [file_id],
        desired_termination_state: 'COMPLETED',
        parser_overrides: [
          {
            file_type: 'json',
            json_parsing_mode: JSONGenerationJobParsingMode.XLSX_JSON_MINIFIER,
          },
        ],
        metadata: {
          document_reference_id,
          measurement_date_id,
          file_id,
        },
      });

      // Start the app-level polling loop to track this job across navigation.
      manuallyExecutePoll(POLL_TYPE.GENERATION_JOBS);

      successNotification('Reprocessing started. New suggestions will appear when complete.');

      // Close menu after successful creation
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      Logger.error('Error reprocessing document:', err);
      const errorMessage = err?.body?.detail || err?.message || 'Failed to reprocess document';
      errorNotification(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessCancel = () => {
    setReprocessDialogOpen(false);
  };

  // Check if the job has metadata (required for auto-completion)
  const hasMetadata =
    generationJob.metadata &&
    generationJob.metadata.document_reference_id &&
    generationJob.metadata.measurement_date_id;

  if (!hasMetadata) {
    return (
      <MenuItem disabled>
        <Box display="flex" alignItems="center" gap={1}>
          No metadata available
        </Box>
      </MenuItem>
    );
  }

  return (
    <>
      <MenuItem onClick={handleCreateMapping} disabled={loading || generationJob.status !== RetrieveDocumentGenerationJob.status.COMPLETED}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          {loading ? 'Creating Mapping...' : 'Create Financial Mapping'}
        </Box>
      </MenuItem>

      <MenuItem onClick={handleReprocessClick} disabled={loading || generationJob.status !== RetrieveDocumentGenerationJob.status.COMPLETED}>
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          Reprocess Document
        </Box>
      </MenuItem>

      <Dialog open={reprocessDialogOpen} onClose={handleReprocessCancel}>
        <DialogTitle>Reprocess Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reprocess the document with the AI mapping system. Accepted and human-created mappings will be preserved.
            Only unaccepted AI suggestions will be replaced with new ones.
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleReprocessConfirm} color="primary" variant="contained">
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentGenerationJobTableItemFinMapMenu;
